import { useMemo, useState } from 'react'
import { Alert, Button, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { HistoryOutlined, WarningOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError, type Translate } from '@/shared/api/errors'
import { formatDateTime, formatDuration, formatNumber, utcTooltip } from '@/shared/lib/format'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { UrlSearchInput } from '@/shared/ui/UrlSearchInput'
import { RowActions } from '@/shared/ui/table/RowActions'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { JobAlertsPanel } from '@/features/jobs/JobAlertsPanel'
import { JobRunsDrawer } from '@/features/jobs/JobRunsDrawer'
import {
  JOB_STATES,
  fetchJobs,
  jobMessageText,
  jobState,
  needsAttention,
  type Job,
  type JobOutcome,
  type JobState,
} from '@/features/jobs/jobsApi'

/** Reestr o'zi yangilanadi — operator sahifani ochib qoldirib ketadi */

/**
 * Rang holatning SABABINI ko'rsatadi, og'irligini emas.
 *
 * ⚠️ `IDLE` KULRANG — bu eng muhim satr. "Yurdi, lekin ishi yo'q edi"
 * ko'p vazifa uchun kunning ko'p qismidagi ODATIY holat. Uni sariq yoki
 * qizil qilish sahifani doimo "kasal" ko'rsatardi va bir haftadan keyin
 * hech kim ranglarga qaramasdi — ya'ni haqiqiy nosozlik ham
 * ko'rinmasdi.
 *
 * ⚠️ `DEAD` va `FAILED` ikkalasi ham qizil, lekin `DEAD` bundan
 * TASHQARI sahifa tepasida alohida banner oladi va qatorda ogohlantirish
 * belgisi bilan chiziladi. Uni faqat rang bilan ajratib bo'lmasdi:
 * qizil allaqachon band, ikkinchi qizil ohang esa ekranda farq
 * bermasdi.
 */
const STATE_COLOR: Record<JobState, string> = {
  DEAD: 'red',
  NEVER: 'volcano',
  FAILED: 'red',
  OVERDUE: 'orange',
  PARTIAL: 'gold',
  IDLE: 'default',
  OK: 'green',
}

/** `JobRunsDrawer` dagi bilan AYNI xarita va ayni sabab (`NOTHING` kulrang) */
const OUTCOME_COLOR: Record<JobOutcome, string> = {
  OK: 'green',
  NOTHING: 'default',
  PARTIAL: 'gold',
  FAILED: 'red',
}

/**
 * Davomiylik.
 *
 * ⚠️ Sekunddan kichigi SEKUNDGA aylantirilmaydi: `formatDuration(0)`
 * "0 son" beradi va u "o'lchanmagan" degan noto'g'ri taassurot
 * qoldiradi. Ko'p job millisekundlarda tugaydi — bu ularning ODATIY
 * holati, kamchilik emas.
 */
function durationLabel(ms: number | null, t: Translate): string {
  if (ms === null) return t('common.dash')
  if (ms < 1000) return t('jobs.msValue', { value: formatNumber(ms) })
  return formatDuration(Math.round(ms / 1000))
}

/**
 * Fon vazifalari holati — QARORLAR.md §20.1.
 *
 * <h2>⚠️ NEGA BU SAHIFA BOR — 2026-08-13</h2>
 *
 * `EventPullJob` qurilma `active=false` bo'lgani uchun kun bo'yi hech
 * narsa qilmadi. U muvaffaqiyatda log yozmaydi, ya'ni "jurnalda yo'q"
 * belgisi IKKI xil narsani anglatardi — "yurdi, ishi yo'q edi" va
 * "umuman yurmadi". Ularni ajratib bo'lmasdi va kamera hodisalari kun
 * bo'yi kelmadi.
 *
 * <h2>⚠️ "SOG'LIQ BALLI" YO'Q</h2>
 *
 * Bu yerda 0..100 ball ham, "umumiy holat" indikatori ham YO'Q va
 * bo'lmaydi. Bir songa siqilgan holat aynan o'sha ajratmani qaytadan
 * yo'q qilardi: "78%" degan raqam o'lgan job bilan bo'sh navbatni bir
 * xil ko'rsatardi. Ustunlar — serverdan kelgan XOM maydonlar,
 * `Holat` ustuni esa ularning to'g'ridan-to'g'ri o'qilishi
 * (`jobsApi.jobState`), yig'indisi emas.
 *
 * <h2>⚠️ `DataTable` EMAS, oddiy antd `Table`</h2>
 *
 * `GET /api/jobs` SAHIFALANMAYDI (`JobController#registry`: har jobga
 * bitta qator, jadval o'smaydi). Sahifalanadigan jadval komponentini
 * unga ulash SOXTA SHARTNOMA bo'lardi. Yurish TARIXI esa
 * sahifalanadi — u `JobRunsDrawer` ichida, `DataTable` bilan.
 *
 * <h2>⚠️ SARALASH SERVERDAN</h2>
 *
 * Qatorlar `JobRegistryRepository#registry` da allaqachon "muammolilari
 * tepada" tartibida keladi. Panel ularni QAYTA saralamaydi: ikkinchi
 * tartib qoidasi paydo bo'lsa, ikkalasi vaqt o'tib ajralib ketardi.
 * Operator ustun sarlavhasidan o'zi saralashi mumkin — u holda bu
 * uning ONGLI tanlovi.
 *
 * <h2>⚠️ "HOZIR ISHGA TUSHIR" TUGMASI YO'Q</h2>
 *
 * Backendda ham unday endpoint yo'q va bo'lmaydi (`JobController`
 * javadoc'i): qo'lda bir marta yurgizish asosiy sababni YASHIRARDI —
 * qator yashil bo'lib qolardi, `@EnableScheduling` esa hamon yo'q
 * bo'lardi.
 *
 * <p>⚠️ 2026-08-20 da qatorga «uch nuqta» menyusi qo'shilganda bu
 * qoida QAYTA TEKSHIRILDI (ADR-028). Menyuda FAQAT O'QISH amali bor —
 * yurishlar tarixini ochish. Jobni ishga tushiradigan, to'xtatadigan
 * yoki jadvalini o'zgartiradigan band QO'SHILMADI:
 * <ul>
 *   <li>{@code JobController} da bunday endpoint umuman yo'q —
 *       faqat {@code @GetMapping};</li>
 *   <li>hozir mikrotik o'chiq va abonentlar ulanmagan, ya'ni RADIUS
 *       bilan ishlaydigan jobni bir bosishda yurgizish oqibatini
 *       oldindan aytib bo'lmaydi;</li>
 *   <li>menyu bandi bir bosishda ketadi — bunday amal uchun eng
 *       xavfli joylashuv.</li>
 * </ul>
 *
 * <h2>⚠️ QIDIRUV VA FILTR — MIJOZDA</h2>
 * Reestr TO'LIQ keladi (`GET /api/jobs` parametrsiz), ya'ni filtr
 * hamma qatorni ko'radi va hech nimani yashirmaydi. Holat MANZILDA:
 * «faqat muammolilar» ko'rinishining havolasini yuborish mumkin.
 */
export function JobsPage() {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<Job | null>(null)

  const jobs = useQuery({
    queryKey: queryKeys.jobs.registry,
    queryFn: ({ signal }) => fetchJobs(signal),
  // ⚠️ Avtoyangilanish OLIB TASHLANDI (egasi qarori 2026-08-20):
  //    u faqat bosh sahifada qoladi. Qolgan ekranlarda u backendga
  //    doimiy yuk berardi, holbuki operator sahifani ochiq qoldirib
  //    boshqa ish bilan band bo'ladi. Yangilash QO'LDA — tugma bor.
  })

  const rows = useMemo(() => (jobs.data ? [...jobs.data] : []), [jobs.data])
  /*
    ⚠️ `dead` TO'LIQ ro'yxatdan hisoblanadi, filtrlangandan EMAS.
    Aks holda filtr qo'yilganda "hech kim ishga tushirmayapti" degan
    eng baland signal jimgina yo'qolardi — va u aynan filtr ostida
    yashirinishi mumkin bo'lgan nosozlik.
  */
  const dead = useMemo(() => rows.filter((row) => !row.scheduled), [rows])

  const { state: tableState, patch: patchTable, reset: resetTable } = useTableUrlState()
  const stateFilter = tableState.filters['state']?.[0]
  const query = tableState.search.trim().toLowerCase()

  const visible = useMemo(
    () =>
      rows.filter((row) => {
        // «Muammolilar» — bitta tanlov ostidagi TO'RT holat. Operator
        // odatda aynan shuni so'raydi: "nimaga qarashim kerak?"
        if (stateFilter === 'attention' && !needsAttention(jobState(row))) return false
        if (
          stateFilter !== undefined
          && stateFilter !== 'attention'
          && jobState(row) !== stateFilter
        ) {
          return false
        }
        if (query === '') return true
        return row.job.toLowerCase().includes(query) || row.service.toLowerCase().includes(query)
      }),
    [rows, stateFilter, query],
  )

  const isFiltered = query !== '' || stateFilter !== undefined

  const columns = useMemo<ColumnsType<Job>>(
    () => [
      {
        key: 'state',
        title: t('jobs.columns.state'),
        width: 170,
        render: (_, row) => {
          const state = jobState(row)
          return (
            <Tooltip title={t(`jobs.stateHint.${state}`)}>
              <Tag
                color={STATE_COLOR[state]}
                icon={needsAttention(state) ? <WarningOutlined /> : undefined}
              >
                {t(`jobs.state.${state}`)}
              </Tag>
            </Tooltip>
          )
        },
      },
      {
        key: 'job',
        title: t('jobs.columns.job'),
        width: 260,
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{row.job}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.service}
            </Typography.Text>
          </Space>
        ),
      },
      {
        key: 'schedule',
        title: t('jobs.columns.schedule'),
        width: 200,
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            {row.schedule ? (
              <Typography.Text code style={{ fontSize: 12 }}>
                {row.schedule}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary" italic>
                {t('jobs.noSchedule')}
              </Typography.Text>
            )}
            {/* Servis oxirgi marta qachon ko'tarilib bu jobni e'lon
                qilgan. Eskirgan `seenAt` "servis o'zi yiqilgan" degan
                BOSHQA nosozlikni ko'rsatadi va uni jobning o'zi bilan
                chalkashtirmaslik kerak. */}
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12 }}
              title={utcTooltip(row.seenAt)}
            >
              {t('jobs.seenAt', { at: formatDateTime(row.seenAt) })}
            </Typography.Text>
          </Space>
        ),
      },
      {
        key: 'lastStartedAt',
        title: t('jobs.columns.lastRun'),
        width: 200,
        render: (_, row) =>
          row.lastStartedAt === null ? (
            // ⚠️ Bo'sh katak EMAS: "hech qachon yurmagan" — YO'QLIK emas,
            // MUAMMO, va u shu so'z bilan aytilishi kerak.
            <Typography.Text type="danger">{t('jobs.neverRan')}</Typography.Text>
          ) : (
            <Space direction="vertical" size={0}>
              <Typography.Text title={utcTooltip(row.lastStartedAt)}>
                {formatDateTime(row.lastStartedAt)}
              </Typography.Text>
              {row.staleSeconds === null ? null : (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('jobs.staleFor', { duration: formatDuration(row.staleSeconds) })}
                </Typography.Text>
              )}
            </Space>
          ),
      },
      {
        key: 'lastOutcome',
        title: t('jobs.columns.outcome'),
        width: 170,
        render: (_, row) =>
          row.lastOutcome === null ? (
            t('common.dash')
          ) : (
            <Space direction="vertical" size={2}>
              <Tooltip title={t(`jobs.outcomeHint.${row.lastOutcome}`)}>
                <Tag color={OUTCOME_COLOR[row.lastOutcome]}>
                  {t(`jobs.outcome.${row.lastOutcome}`)}
                </Tag>
              </Tooltip>
              {row.lastItems === null ? null : (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('jobs.itemsValue', { value: formatNumber(row.lastItems) })}
                </Typography.Text>
              )}
            </Space>
          ),
      },
      {
        key: 'lastOkAt',
        title: t('jobs.columns.lastOk'),
        width: 190,
        render: (_, row) =>
          row.lastOkAt === null ? (
            <Typography.Text type="secondary">{t('jobs.never')}</Typography.Text>
          ) : (
            // ⚠️ TOOLTIP MAJBURIY: bu ustun "oxirgi marta ISH BAJARDI"
            // deb o'qilishi juda oson, holbuki `NOTHING` ham uni
            // yangilaydi. Aynan shu noto'g'ri o'qish 2026-08-13 ni
            // takrorlardi.
            <Tooltip title={t('jobs.lastOkHint')}>
              <Typography.Text title={utcTooltip(row.lastOkAt)}>
                {formatDateTime(row.lastOkAt)}
              </Typography.Text>
            </Tooltip>
          ),
      },
      {
        key: 'lastDurationMs',
        title: t('jobs.columns.duration'),
        width: 130,
        align: 'right',
        render: (_, row) => durationLabel(row.lastDurationMs, t),
      },
      {
        key: 'runs',
        title: t('jobs.columns.runs'),
        width: 110,
        align: 'right',
        render: (_, row) => formatNumber(row.runs),
      },
      {
        key: 'message',
        title: t('jobs.columns.message'),
        // ⚠️ Xato XABARDAN USTUN: yiqilgan yurishda ikkalasi ham
        // bo'lishi mumkin, operatorga kerakligi — sabab.
        render: (_, row) =>
          row.lastError ? (
            <Typography.Text type="danger" ellipsis={{ tooltip: row.lastError }}>
              {row.lastError}
            </Typography.Text>
          ) : (
            // ⚠️ Kod bo'yicha tarjima, matn — ZAXIRA (N5).
            (jobMessageText(row.lastMessageCode, row.lastMessageParams, row.lastMessage, t)
              ?? t('common.dash'))
          ),
      },
      {
        /*
          ⚠️ MENYUDA BITTA BAND VA U FAQAT O'QIYDI (ADR-028).

          «Yurishlar tarixi» qatorni bosish bilan bir xil ishni
          qiladi — menyu uni shunchaki KO'RINADIGAN qiladi: qator
          bosiladigan ekanini hech narsa aytmasdi.

          ⚠️ Bu yerga jobni ISHGA TUSHIRADIGAN band QO'SHILMAYDI.
          Sabab sinf javadoc'ida: endpoint yo'q, mikrotik o'chiq va
          menyu bandi bir bosishda ketadi.
        */
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          <RowActions
            items={[
              {
                key: 'history',
                label: t('jobs.openHistory'),
                icon: <HistoryOutlined />,
                onClick: () => setSelected(row),
              },
            ]}
          />
        ),
      },
    ],
    [t],
  )

  return (
    <>
      <PageHeader title={t('nav.jobs')} description={t('pageIntro.jobs')} />

      {/*
        ⚠️ QOROVUL REESTRDAN YUQORIDA. `JobController.alerts` javadoc'i
        buni talab qiladi va sabab pastdagi jadvalning O'ZIDA: reestr
        yashil bo'lishi mumkin, holbuki uni hech kim TEKSHIRMAYAPTI.
        Bunday holatda pastdagi yashil qatorlarga ishonib bo'lmaydi va
        operator buni jadvalga qaragunicha BILISHI kerak.
      */}
      <JobAlertsPanel />

      <Typography.Title level={5}>{t('jobs.registryTitle')}</Typography.Title>

      {jobs.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(jobs.error, t)}
        />
      ) : null}

      {/* ⚠️ SAHIFADAGI ENG BALAND SIGNAL. `scheduled = false` — klass
          bor, bean bor, kod to'g'ri, lekin rejalashtiruvchi uni hech
          qachon ishga tushirmaydi. Bu eng JIM nosozlik turi: xato yo'q,
          jurnal bo'sh, hamma narsa joyida ko'rinadi. Jadvaldagi qizil
          teg yetarli emas edi — qator ro'yxatning ichida, banner esa
          sahifa ochilishi bilan ko'zga tushadi. */}
      {dead.length > 0 ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('jobs.deadTitle', { n: dead.length })}
          description={
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Typography.Text>{t('jobs.deadHint')}</Typography.Text>
              <Typography.Text code style={{ fontSize: 12 }}>
                {dead.map((row) => `${row.service}/${row.job}`).join(' · ')}
              </Typography.Text>
            </Space>
          }
        />
      ) : null}

      <Space wrap style={{ marginBottom: 16 }}>
        <UrlSearchInput placeholder={t('jobs.searchPlaceholder')} width={260} />
        <Select
          allowClear
          style={{ minWidth: 220 }}
          placeholder={t('jobs.columns.state')}
          value={stateFilter}
          onChange={(v?: string) =>
            patchTable({ filters: { ...tableState.filters, state: v ? [v] : [] } })
          }
          options={[
            { value: 'attention', label: t('jobs.onlyAttention') },
            ...JOB_STATES.map((s) => ({ value: s, label: t(`jobs.state.${s}`) })),
          ]}
        />
        {isFiltered ? (
          <Space size={4}>
            <Tag color="blue">
              {t('roles.shownOfTotal', { shown: visible.length, total: rows.length })}
            </Tag>
            <Button size="small" type="link" onClick={resetTable}>
              {t('dataTable.clearFilters')}
            </Button>
          </Space>
        ) : null}
      </Space>

      <Table<Job>
        dataSource={visible}
        columns={columns}
        rowKey={(row) => `${row.service}/${row.job}`}
        loading={jobs.isPending}
        size="small"
        // ⚠️ SAHIFALASH O'CHIRILGAN — endpoint uni bermaydi va jadval
        // o'smaydi. Mijoz tomonda sahifalash "server sahifalayapti"
        // degan yolg'on taassurot qoldirardi.
        pagination={false}
        scroll={{ x: 1616 }}
        onRow={(row) => ({
          onClick: () => setSelected(row),
          style: { cursor: 'pointer' },
        })}
        locale={{
          emptyText: jobs.isError ? (
            <EmptyState variant="error" title={t('jobs.loadFailed')} />
          ) : isFiltered ? (
            /*
              ⚠️ FILTR HECH NARSA TOPMAGANI «reestr bo'sh» DEGANI EMAS
              va bu farq bu sahifada odatdagidan ham muhim: pastdagi
              matn "hech bir servis qayta ishga tushmagan" deb
              tashvishga soladi, holbuki sabab shunchaki tanlangan
              holat filtri bo'lishi mumkin.
            */
            <EmptyState
              variant="no-data"
              title={t('dataTable.emptyFiltered')}
              description={t('dataTable.emptyFilteredHint')}
              actionLabel={t('dataTable.resetFilters')}
              onAction={resetTable}
            />
          ) : (
            // ⚠️ BO'SH REESTR — "hammasi joyida" DEGANI EMAS. Reestrga
            // servis KO'TARILGANDA yozadi (`JobRegistrar`), ya'ni bo'sh
            // ro'yxat "migratsiya qo'llanmagan yoki hech bir servis
            // qayta ishga tushmagan" degani. Buni "ma'lumot yo'q" deb
            // ko'rsatish sahifaning butun maqsadiga zid bo'lardi.
            <EmptyState
              variant="no-device"
              title={t('jobs.empty')}
              description={t('jobs.emptyHint')}
            />
          ),
        }}
      />

      <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
        {t('jobs.note')}
      </Typography.Paragraph>

      <JobRunsDrawer job={selected} onClose={() => setSelected(null)} />
    </>
  )
}
